// 'use client'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress, IconButton } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CloseIcon from '@mui/icons-material/Close';
import { useLanguage } from '../../../utils/i18n';

export default function LockConfirmDialog({
  open = false,
  action = 'lock', // 'lock' | 'unlock'
  title, // optional override
  message, // optional override
  loading = false,
  onClose = () => {},
  onConfirm = () => {},
  confirmLabel, // optional override
  cancelLabel = 'Cancel',
  itemName = ''
}) {
  const isLock = String(action) === 'lock';
  const { t } = useLanguage();
  const defaultTitle = isLock ? t('masterUser.confirmLock', 'Confirm Lock') : t('masterUser.confirmUnlock', 'Confirm Unlock');
  const defaultMessage = isLock
    ? t('masterUser.message1', 'You are about to lock this user. They will not be able to access the system until unlocked.')
    : t('masterUser.message2', 'You are about to unlock this user. They will regain access to the system.');

  const label = confirmLabel ?? (isLock ? t('general.lockUser', 'Lock User') : t('general.unlockUser', 'Unlock User'));

  const iconBg = isLock
    ? {
        background: 'linear-gradient(135deg, rgba(244,67,54,0.08), rgba(244,67,54,0.04))',
        border: '1px solid rgba(244,67,54,0.12)'
      }
    : {
        background: 'linear-gradient(135deg, rgba(76,175,80,0.08), rgba(76,175,80,0.04))',
        border: '1px solid rgba(76,175,80,0.12)'
      };

  const iconColor = isLock ? '#f44336' : '#43a047';

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 0,
          overflow: 'visible',
          boxShadow: '0 10px 30px rgba(8,14,30,0.12)'
        }
      }}
      aria-labelledby="lock-confirm-title"
      aria-describedby="lock-confirm-description"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...iconBg
            }}
            aria-hidden="true"
          >
            {isLock ? <LockIcon sx={{ color: iconColor, fontSize: 28 }} /> : <LockOpenIcon sx={{ color: iconColor, fontSize: 28 }} />}
          </Box>

          <DialogTitle id="lock-confirm-title" sx={{ p: 0, fontWeight: 700 }}>
            {title ?? defaultTitle}
          </DialogTitle>
        </Box>

        <IconButton onClick={() => !loading && onClose()} size="small" sx={{ color: 'text.secondary' }} aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Typography id="lock-confirm-description" variant="body1" sx={{ mb: 1.25 }}>
          {message ?? defaultMessage}
        </Typography>

        {itemName ? (
          <Box
            sx={{
              mt: 0.5,
              p: 1,
              borderRadius: 1,
              backgroundColor: 'action.hover',
              display: 'inline-block'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {itemName}
            </Typography>
          </Box>
        ) : null}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {isLock
            ? t('masterUser.message3', 'This action can be reversed by unlocking the user later.')
            : t('masterUser.message4', 'The user will be able to access the system after unlocking.')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            borderRadius: 1.5,
            px: 2,
            '&:hover': { boxShadow: 'none' }
          }}
        >
          {cancelLabel}
        </Button>

        <Button
          variant="contained"
          color={isLock ? 'error' : 'success'}
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress color="inherit" size={18} thickness={5} /> : null}
          sx={{
            ml: 1,
            textTransform: 'none',
            borderRadius: 1.5,
            px: 2.5,
            fontWeight: 600,
            boxShadow: loading ? 'none' : isLock ? '0 6px 18px rgba(211,47,47,0.16)' : '0 6px 18px rgba(67,160,71,0.12)',
            '&:hover': {
              transform: loading ? 'none' : 'translateY(-1px)'
            }
          }}
        >
          {loading ? (isLock ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.processing', 'Processing...')) : label}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
